/**
 * keptRangesTimeline — geometry and grouping for the kept older-order ranges.
 *
 * TCA-26100804. Pure: it takes ranges and a reference date and returns
 * positions, ticks and year groups. The caller supplies "today" rather than the
 * module reading the clock, so the output is testable and a stale render cannot
 * silently move the axis.
 *
 * Dates are plain ISO days (YYYY-MM-DD) and are handled in UTC throughout. A
 * local-time reading would shift a range by a day either side of midnight.
 */

const DAY_MS = 86400000;
const MONTH_NAMES = Object.freeze([
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]);

// A label this close to the right edge would collide with the Today marker.
const EDGE_LABEL_CUTOFF = 94;
// Beyond this span the month axis becomes unreadable and the scale switches.
const MONTH_MODE_MONTHS = 14;
// Keep a very short range visible rather than letting it vanish to a hairline.
const MIN_SEGMENT_PERCENT = 0.8;

function parseDay(iso) {
  if (typeof iso !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!match) return null;
  const [, y, m, d] = match;
  const value = Date.UTC(Number(y), Number(m) - 1, Number(d));
  return Number.isFinite(value) ? value : null;
}

function utcYear(ms) { return new Date(ms).getUTCFullYear(); }
function utcMonth(ms) { return new Date(ms).getUTCMonth(); }
function utcDate(ms) { return new Date(ms).getUTCDate(); }

export function formatRangeLabel(fromMs, toMs) {
  const from = `${MONTH_NAMES[utcMonth(fromMs)]} ${utcDate(fromMs)}`;
  const to = `${MONTH_NAMES[utcMonth(toMs)]} ${utcDate(toMs)}`;
  // A range crossing a year boundary states the end year, so "Nov 2 – Jan 10"
  // cannot be read as ending ten months earlier than it does.
  return utcYear(fromMs) === utcYear(toMs)
    ? `${from} – ${to}`
    : `${from} – ${to}, ${utcYear(toMs)}`;
}

export function dayCount(fromMs, toMs) {
  return Math.round((toMs - fromMs) / DAY_MS) + 1;
}

/**
 * @param {Array<{dateFrom: string, dateTo: string, orderCount: number|null}>} ranges
 * @param {string} todayIso reference day; the axis ends here
 */
export function buildKeptRangesTimeline(ranges, todayIso) {
  const today = parseDay(todayIso);
  const parsed = (Array.isArray(ranges) ? ranges : [])
    .map((item) => {
      const from = parseDay(item?.dateFrom);
      const to = parseDay(item?.dateTo);
      if (from === null || to === null || to < from) return null;
      return { from, to, orderCount: Number.isInteger(item?.orderCount) ? item.orderCount : null };
    })
    .filter(Boolean)
    .sort((a, b) => a.from - b.from);

  const totals = Object.freeze({
    rangeCount: parsed.length,
    orderCount: parsed.reduce((sum, r) => sum + (r.orderCount || 0), 0),
  });

  if (today === null || parsed.length === 0) {
    return Object.freeze({ mode: 'empty', ticks: [], segments: [], groups: [], totals });
  }

  const oldest = parsed[0].from;
  const months = (utcYear(today) - utcYear(oldest)) * 12 + (utcMonth(today) - utcMonth(oldest));
  const mode = months <= MONTH_MODE_MONTHS ? 'months' : 'years';

  // The axis starts at the oldest range's month, or at Jan 1 of its year in
  // year mode, so ticks land on whole boundaries rather than arbitrary days.
  const start = mode === 'months'
    ? Date.UTC(utcYear(oldest), utcMonth(oldest), 1)
    : Date.UTC(utcYear(oldest), 0, 1);
  const span = Math.max(today - start, DAY_MS);
  const position = (ms) => ((ms - start) / span) * 100;

  const ticks = [];
  if (mode === 'months') {
    for (let y = utcYear(start), m = utcMonth(start); ;) {
      const at = Date.UTC(y, m, 1);
      if (at > today) break;
      const pos = position(at);
      ticks.push(Object.freeze({
        pos, major: m === 0, label: pos < EDGE_LABEL_CUTOFF ? MONTH_NAMES[m] : null,
      }));
      m += 1;
      if (m > 11) { m = 0; y += 1; }
    }
  } else {
    for (let y = utcYear(start); ; y += 1) {
      for (let q = 0; q < 4; q += 1) {
        const at = Date.UTC(y, q * 3, 1);
        if (at > today) { q = 4; y = Infinity; break; }
        const pos = position(at);
        ticks.push(Object.freeze({
          pos,
          major: q === 0,
          label: q === 0 && pos < EDGE_LABEL_CUTOFF ? String(y) : null,
        }));
      }
      if (y === Infinity) break;
    }
  }

  const segments = parsed.map((r) => {
    const left = position(r.from);
    const width = Math.max(position(r.to + DAY_MS) - left, MIN_SEGMENT_PERCENT);
    return Object.freeze({
      left,
      width: Math.min(width, 100 - left),
      key: `${r.from}:${r.to}`,
    });
  });

  const groups = [];
  for (const r of parsed) {
    const year = utcYear(r.from);
    if (!groups.length || groups[groups.length - 1].year !== year) {
      groups.push({ year, rows: [] });
    }
    groups[groups.length - 1].rows.push(Object.freeze({
      key: `${r.from}:${r.to}`,
      label: formatRangeLabel(r.from, r.to),
      days: dayCount(r.from, r.to),
      orderCount: r.orderCount,
    }));
  }

  return Object.freeze({
    mode,
    ticks: Object.freeze(ticks),
    segments: Object.freeze(segments),
    groups: Object.freeze(groups.map((g) => Object.freeze({ ...g, rows: Object.freeze(g.rows) }))),
    totals,
  });
}
